// Added by Sejune Oh on 2019-06-12
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import Select, { components } from 'react-select'
import styled from 'styled-components'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { GREY_COLOR } from '@/styles/Theme'
import { Checkbox } from '@/components/common/v2'
import { AddBoxIcon } from '@/components/_primes'
import MultiSelect from './MultiSelect'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface MultiSelectBaseCProps {
  title: string
  isRequired?: boolean
}

interface MultiSelectCProps extends MultiSelectBaseCProps {
  options: any[]
  value: any[]
  valueProp?: string
  labelProp?: string
  onSelect?: Function
  isFullWidth?: boolean
  hasNotCondition?: boolean
  disabled?: boolean
  description?: string
  isSelectStyle?: boolean
}

export interface MultiSelectItemProps {
  value: string | number
  label: string
  tooltip?: string
}

const CustomTootipElement = styled.div`
  && {
    &[data-tooltip-text]:hover {
      position: relative;
    }

    &[data-tooltip-text]:hover:after {
      content: attr(data-tooltip-text);
      position: absolute;
      right: 12px;
      top: 50%;
      margin-top: -6px;
      height: 12px;
      font-size: 12px;
      z-index: 9999;
    }
  }
`

const CustomTootip = (props: any) => {
  return (
    <CustomTootipElement data-tooltip-text={props.data.tooltip || ''}>
      <components.Option {...props} />
    </CustomTootipElement>
  )
}

const CustomClearIndicator = (props: any) => {
  // @ts-ignore
  const clearIndicator = <components.ClearIndicator {...props} />
  return <div data-cy-btn='clearAllGroup'>{clearIndicator}</div>
}

const CustomMultiValueContainer = ({ children, innerProps, setDefaultValue, ...props }: any) => {
  const multiValue = props.data.value
  const isNot = typeof multiValue === 'string' ? multiValue.includes('!') : false

  const onMouseDown = (e: any) => {
    e.stopPropagation()

    setDefaultValue((prev: any) => {
      let filtered = prev.filter((prevItem: any) => prevItem.value === multiValue)[0]
      if (filtered.value.includes('!')) {
        filtered = { ...filtered, value: filtered.value.replace('!', '') }
      } else {
        filtered = { ...filtered, value: `!${filtered.value}` }
      }
      const filteredIdx = prev.findIndex((prevItem: any) => prevItem.label === filtered.label)
      const next = [...prev]
      next.splice(filteredIdx, 1, filtered)
      return next
    })
  }

  const innerPropsWithAction = { ...innerProps, onMouseDown }

  return (
    <components.MultiValueContainer innerProps={innerPropsWithAction} {...props}>
      <Checkbox checked={!isNot} checkedIcon={<AddBoxIcon />} indeterminate={isNot} labelStyle={{ padding: 0 }} />
      {children}
    </components.MultiValueContainer>
  )
}

const MultiSelectContainer = forwardRef(
  (
    {
      value,
      options: propsOptions,
      valueProp,
      labelProp,
      title,
      isRequired,
      isFullWidth,
      disabled,
      onSelect,
      description,
      hasNotCondition,
      isSelectStyle,
    }: MultiSelectCProps,
    ref
  ) => {
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // states
    const [defaultValue, setDefaultValue] = useState<MultiSelectItemProps[]>([])
    const [options, setOptions] = useState<MultiSelectItemProps[]>([])
    const [isErrorOccured, setIsErrorOccured] = useState<boolean>(false)
    const [errMsg, setErrMsg] = useState<string>('')
    const [updateFlag, setUpdateFlag] = useState<boolean>(false)

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // refs
    const selectRef = useRef<any>()

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // effects
    useEffect(() => {
      const values = getItems(value)
      setDefaultValue([...values])
      const defaultOption = [...getItems(propsOptions), ...values]
      setOptions(defaultOption)
    }, [value, propsOptions])

    useEffect(() => {
      if (updateFlag) {
        setTimeout(() => {
          const values = getItems(value)
          setDefaultValue([...values])
          const defaultOption = [...getItems(propsOptions), ...values]
          setOptions(defaultOption)
          setUpdateFlag(false)
        }, 0)
      }
    }, [updateFlag])

    useImperativeHandle(
      ref,
      () => ({
        forceRerender: () => {
          setUpdateFlag(true)
        },
        getValue,
        setError,
      }),
      [ref, value, defaultValue]
    )

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // handler
    const getItems = (items: any[]) => {
      return items.map((item) => {
        if (typeof item === 'string') {
          return {
            value: item,
            label: item,
          }
        }

        return {
          value: item[valueProp || 'value'],
          label: item[labelProp || 'label'],
          tooltip: item.tooltip || undefined,
        }
      })
    }

    const getValue = () => {
      return selectRef.current.props.value || []
    }

    const handleChange = (value: any, action: any) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!onSelect) {
        onSelect(value, [...getValue()], action)
      }

      setDefaultValue(value)

      setError()
    }

    const setError = (errMessage?: string) => {
      if (errMessage) {
        setIsErrorOccured(true)
        setErrMsg(errMessage)
      } else {
        setIsErrorOccured(false)
        setErrMsg('')
      }
    }

    return (
      <MultiSelect
        title={title}
        isErrorOccured={isErrorOccured}
        errMessage={errMsg}
        isRequired={isRequired}
        isFullWidth={isFullWidth}
        disabled={disabled}
        isSelectStyle={isSelectStyle}
      >
        {updateFlag ? (
          <EmptySpan />
        ) : (
          <Select
            ref={selectRef}
            styles={customStyles}
            value={defaultValue}
            closeMenuOnSelect={false}
            onChange={handleChange}
            options={options}
            isDisabled={disabled}
            components={{
              Option: CustomTootip,
              ClearIndicator: CustomClearIndicator,
              ...(hasNotCondition && {
                MultiValueContainer: (props) => (
                  <CustomMultiValueContainer setDefaultValue={setDefaultValue} {...props} />
                ),
              }),
            }}
            noOptionsMessage={() => Intl.getIntlText({ id: 'htmljs_react.component.multiselectcontainer.no_options' })}
            menuPosition={'fixed'} // TODO: @Jonghyung 현재 fix 상태에 대한 이러한 이슈가 있음 https://github.com/JedWatson/react-select/issues/4088
            isMulti
          />
        )}
        {description && <Descriptoin>{description}</Descriptoin>}
      </MultiSelect>
    )
  }
)

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const customStyles = {
  option: (provided: any) => ({
    ...provided,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#fff',
    overflow: 'hidden',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'content-box',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    width: 'auto',
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  }),
  container: (provided: any) => ({
    ...provided,
    transition:
      'padding-left 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,border-width 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    border: '1px solid rgba(255, 255, 255, 0.23)',
    borderRadius: '4px',
    '&:hover': {
      borderColor: '#fff',
    },
    paddingLeft: '6px',
  }),
  control: () => ({
    display: 'flex',
    cursor: 'pointer',
    paddingBottom: '6px',
    '&>div:first-of-type': {
      padding: 0,
    },
  }),
  input: (provided: any) => ({
    ...provided,
    color: '#fff',
  }),
  multiValue: (provided: any, state: any) => {
    return {
      ...provided,
      display: 'flex',
      color: state.isDisabled ? 'rgba(255, 255, 255, 0.2)' : '#fff',
      backgroundColor: `${GREY_COLOR}`,
      border: '1px solid #676767',
      borderRadius: '4px',
      padding: '3px',
      marginRight: '5px',
      marginTop: '5px',
      '&>div': {
        color: 'inherit',
        borderRadius: 0,
        fontSize: '1rem',
        transition: 'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      },
      '&>div:last-child:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '50%',
        color: '#fff',
      },
    }
  },
  menu: (provided: any) => ({
    ...provided,
    background: `${GREY_COLOR}`,
    color: '#fff',
    zIndex: 2,
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
  }),
  placeholder: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided: any, state: any) => {
    const dividerDisplay = state.hasValue ? 'flex' : 'none'
    return {
      ...provided,
      marginBottom: '-4px',
      '&>div:first-of-type': {
        order: 2,
      },
      '&>span': {
        display: dividerDisplay,
      },
      '&>div:last-child': {
        order: 0,
      },
    }
  },
  indicatorSeparator: (provided: any, state: any) => {
    const width = state.isDisabled ? 0 : 1
    return {
      ...provided,
      width,
      order: 1,
    }
  },
}

const EmptySpan = styled.span`
  && {
    padding: 12px;
    position: relative;
    min-width: 50%;
  }
`

const Descriptoin = styled.p`
  && {
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    padding: 0.5rem 0.3rem 0;
  }
`

export default MultiSelectContainer
